import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@components';

interface Schedule {
  id: string;
  days: string[];
  startTime: string;
  endTime: string;
}

interface BusinessHoursPickerProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

const WEEKDAYS = [
  { value: 'Mon', label: 'Mo' },
  { value: 'Tue', label: 'Di' },
  { value: 'Wed', label: 'Mi' },
  { value: 'Thu', label: 'Do' },
  { value: 'Fri', label: 'Fr' },
  { value: 'Sat', label: 'Sa' },
  { value: 'Sun', label: 'So' },
];

const DAY_ORDER: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

export const BusinessHoursPicker: React.FC<BusinessHoursPickerProps> = ({
  value,
  onChange,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState<Schedule[]>([]);

  // Parse business hours string into schedules
  useEffect(() => {
    if (!value || value.trim() === '') {
      setSchedules([]);
      return;
    }

    try {
      const parsed = parseBusinessHours(value);
      setSchedules(parsed);
    } catch (e) {
      // If parsing fails, create empty schedule
      setSchedules([]);
    }
  }, [value]);

  // Convert schedules back to string and notify parent
  const updateBusinessHours = (newSchedules: Schedule[]) => {
    setSchedules(newSchedules);
    const formatted = formatBusinessHours(newSchedules);
    onChange(formatted);
  };

  const addSchedule = () => {
    const newSchedule: Schedule = {
      id: `schedule-${Date.now()}`,
      days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      startTime: '09:00',
      endTime: '17:00',
    };
    updateBusinessHours([...schedules, newSchedule]);
  };

  const removeSchedule = (id: string) => {
    updateBusinessHours(schedules.filter(s => s.id !== id));
  };

  const updateSchedule = (id: string, updates: Partial<Schedule>) => {
    updateBusinessHours(schedules.map(s => (s.id === id ? { ...s, ...updates } : s)));
  };

  const toggleDay = (scheduleId: string, day: string) => {
    const schedule = schedules.find(s => s.id === scheduleId);
    if (!schedule) return;

    const newDays = schedule.days.includes(day)
      ? schedule.days.filter(d => d !== day)
      : [...schedule.days, day].sort((a, b) => DAY_ORDER[a] - DAY_ORDER[b]);

    updateSchedule(scheduleId, { days: newDays });
  };

  return (
    <div className="space-y-4">
      {schedules.map((schedule, index) => (
        <div
          key={schedule.id}
          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3"
        >
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('businessHours.timeSlot')} {index + 1}
            </span>
            {schedules.length > 1 && (
              <button
                type="button"
                onClick={() => removeSchedule(schedule.id)}
                disabled={disabled}
                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm"
              >
                {t('common.remove')}
              </button>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              {t('businessHours.selectDays')}
            </label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map(day => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(schedule.id, day.value)}
                  disabled={disabled}
                  className={`
                    px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                    ${
                      schedule.days.includes(day.value)
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {t('businessHours.startTime')}
              </label>
              <Input
                type="time"
                value={schedule.startTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateSchedule(schedule.id, { startTime: e.target.value })
                }
                disabled={disabled}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {t('businessHours.endTime')}
              </label>
              <Input
                type="time"
                value={schedule.endTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateSchedule(schedule.id, { endTime: e.target.value })
                }
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addSchedule}
        disabled={disabled}
        className="w-full"
      >
        + {t('businessHours.addTimeSlot')}
      </Button>

      {schedules.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            {t('businessHours.preview')}:
          </p>
          <p className="text-sm text-gray-800 dark:text-gray-200 font-mono">
            {formatBusinessHours(schedules)}
          </p>
        </div>
      )}

      {error && <p className="text-sm text-red-500 dark:text-red-400">{error}</p>}
    </div>
  );
};

// Parse "Mon-Fri 9:00-18:30, Sat 10:00-14:00" into Schedule[]
function parseBusinessHours(hoursString: string): Schedule[] {
  const schedules: Schedule[] = [];
  const parts = hoursString.split(',').map(p => p.trim());

  parts.forEach((part, index) => {
    // Match patterns like "Mon-Fri 9:00-18:30" or "Sat 10:00-14:00"
    const match = part.match(/^(\w+)(?:-(\w+))?\s+(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/);
    if (!match) return;

    const [, startDay, endDay, startHour, startMin, endHour, endMin] = match;

    let days: string[] = [];
    if (endDay) {
      // Range like "Mon-Fri"
      const startIdx = DAY_ORDER[startDay];
      const endIdx = DAY_ORDER[endDay];
      days = Object.keys(DAY_ORDER)
        .filter(day => {
          const idx = DAY_ORDER[day];
          return idx >= startIdx && idx <= endIdx;
        })
        .sort((a, b) => DAY_ORDER[a] - DAY_ORDER[b]);
    } else {
      // Single day like "Sat"
      days = [startDay];
    }

    schedules.push({
      id: `schedule-${index}`,
      days,
      startTime: `${startHour.padStart(2, '0')}:${startMin}`,
      endTime: `${endHour.padStart(2, '0')}:${endMin}`,
    });
  });

  return schedules;
}

// Convert Schedule[] to "Mon-Fri 9:00-18:30, Sat 10:00-14:00"
function formatBusinessHours(schedules: Schedule[]): string {
  return schedules
    .filter(s => s.days.length > 0)
    .map(schedule => {
      const sortedDays = [...schedule.days].sort((a, b) => DAY_ORDER[a] - DAY_ORDER[b]);

      // Format time without leading zero for hours
      const formatTime = (time: string) => {
        const [hours, minutes] = time.split(':');
        const formattedHours = parseInt(hours, 10).toString(); // Remove leading zero
        return `${formattedHours}:${minutes}`;
      };

      const timeRange = `${formatTime(schedule.startTime)}-${formatTime(schedule.endTime)}`;

      // Find consecutive day ranges
      const ranges: string[][] = [];
      let currentRange: string[] = [sortedDays[0]];

      for (let i = 1; i < sortedDays.length; i++) {
        const prevDay = sortedDays[i - 1];
        const currentDay = sortedDays[i];

        if (DAY_ORDER[currentDay] === DAY_ORDER[prevDay] + 1) {
          // Consecutive day
          currentRange.push(currentDay);
        } else {
          // Gap detected, save current range and start new one
          ranges.push(currentRange);
          currentRange = [currentDay];
        }
      }
      // Don't forget to add the last range
      ranges.push(currentRange);

      // Format each range
      const formattedRanges = ranges.map(range => {
        if (range.length === 1) {
          return `${range[0]} ${timeRange}`;
        } else {
          return `${range[0]}-${range[range.length - 1]} ${timeRange}`;
        }
      });

      return formattedRanges.join(', ');
    })
    .join(', ');
}
